import {
  CostInsightsApi,
  ProductInsightsOptions,
  Alert,
} from '@backstage-community/plugin-cost-insights';
import {
  Cost,
  Entity,
  Group,
  MetricData,
  Project,
} from '@backstage-community/plugin-cost-insights-common';
import {
  TENANTS,
  PRODUCTS,
  costFor,
  entityOf,
  aggregationFor,
  changeOf,
  hash,
} from './data';

/**
 * Mock-Implementierung des CostInsightsApi-Vertrags fuer die Demo.
 *
 * Die UI spricht nur gegen dieses Interface. Spaeter wird genau dieser Vertrag
 * von einem echten Client erfuellt (OpenCost pro tenant-<name>-Namespace +
 * Cloud-Billing als $-Ground-Truth). Bis dahin liefern wir deterministische,
 * seeded Mock-Daten. Nichts hiervon ruft echte Backends auf.
 */
export class TallenceCostInsightsClient implements CostInsightsApi {
  private readonly billingDate = '2026-06-24';

  async getLastCompleteBillingDate(): Promise<string> {
    return this.billingDate;
  }

  async getUserGroups(_userId: string): Promise<Group[]> {
    return TENANTS.map(t => ({ id: t.id, name: t.name }));
  }

  async getGroupProjects(group: string): Promise<Project[]> {
    const tenant = TENANTS.find(t => t.id === group);
    return (tenant?.services ?? []).map(s => ({ id: s.id, name: s.name }));
  }

  async getGroupDailyCost(group: string, intervals: string): Promise<Cost> {
    const tenant = TENANTS.find(t => t.id === group);
    const baseline = (tenant?.services ?? []).reduce(
      (acc, s) => acc + s.base,
      0,
    );
    return costFor(group, intervals, baseline || 4000, hash(group));
  }

  async getProjectDailyCost(project: string, intervals: string): Promise<Cost> {
    const service = TENANTS.flatMap(t => t.services).find(
      s => s.id === project,
    );
    return costFor(project, intervals, service?.base ?? 1500, hash(project));
  }

  async getDailyMetricData(
    metric: string,
    intervals: string,
  ): Promise<MetricData> {
    const aggregation = aggregationFor(intervals, 18000, hash(metric));
    return {
      id: metric,
      format: 'number',
      aggregation,
      change: changeOf(aggregation),
    };
  }

  async getProductInsights(options: ProductInsightsOptions): Promise<Entity> {
    const product = PRODUCTS.find(p => p.id === options.product);
    const tenant = TENANTS.find(t => t.id === options.group);
    const groupBaseline = tenant
      ? tenant.services.reduce((acc, s) => acc + s.base, 0)
      : 4000;
    const baseline = groupBaseline * (product?.share ?? 0.1);
    return entityOf(
      options.product,
      options.intervals,
      baseline,
      hash(options.group),
    );
  }

  async getAlerts(group: string): Promise<Alert[]> {
    const alerts: Alert[] = [];
    if (group === 'device-status') {
      alerts.push({
        title: 'Idle compute detected in device-status',
        subtitle:
          'Two over-provisioned workloads could save about €340 / month if right-sized.',
      });
    }
    alerts.push({
      title: `Cost for ${group} grew over the last 30 days`,
      subtitle:
        'Spend is trending up. Review the breakdown by product and service below.',
    });
    return alerts;
  }
}
